import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { EntityStore } from "@use/entity-store";

import { urls } from "@courses-pm2/api/src/ts/config";

/**
 * The languages that the SpeechSynthesis supports
 */
export enum SupportedLangauges {
    DANISH = "Danish",
    ENGLISH = "English",
    FRENCH = "French",
    SPANISH = "Spanish",
    GERMAN = "German",
}

/**
 * Specification of data returned about each available portal.
 */
export type PM2PortalConfig = {
    /**
     * Contains the internal SiteCore id of the portal.
     */
    id: string;

    /**
     * Specifies the display-friendly title of the portal.
     */
    title: string;

    /**
     * Unique Id of portal (ISBN-10 or ISBN-13).
     */
    uniqueId: string;

    /**
     * Access keys of the portal (ISBN-10 or ISBN-13).
     */
    accessKeys?: string[];

    /**
     * If enabled, then user doesn't need to own a valid license in order to be
     * granted access to the portal.
     */
    loginFreeAccess: boolean;

    /**
     * Configuration of the SpeechSynthesis for the portal.
     */
    speechSynthesize?: {
        /**
         * Whether speechSynthesis should be enabled or not.
         */
        enabled?: boolean;

        /**
         * Whether highlighting of words should be used while reading.
         */
        highlight?: boolean;

        /**
         * The primary language to be used with the speechSynthesis.
         */
        language?: SupportedLangauges;

        /**
         * Supplies a secondary language to be used with the speechSynthesis.
         */
        secondaryLanguage?: SupportedLangauges;
    };
};

/**
 * Internal cache that'll keep portal configurations in memory for up to 30
 * minutes to prevent fetching the same data over and over again.
 */
const cache = new EntityStore<PM2PortalConfig>({ tti: 1800 });

/**
 * Helper that'll connect fetch a complete list of portals created on the given
 * environment.
 */
export async function getConfig(portalId: string): Promise<PM2PortalConfig> {
    if (!cache.has(portalId)) {
        const response = await axios.get<PM2PortalConfig>(
            gGateway(`${urls.pm2}/v1/portals/${portalId}/configuration`),
            {
                headers: {
                    "X-PM2-Authorized": "False",
                    "X-PM2-Authentication-Type": "None",
                    "X-PM2-User-Role": "None",
                },
            },
        );

        cache.set(portalId, response.data);
    }

    return <PM2PortalConfig>cache.get(portalId);
}
